import {
  Accordion,
  Accordions,
  Freeform,
  Newsletter,
  Video,
  BlogPostCard,
  CalendlyWidget,
  DemoForm,
  Iframe,
  Html,
} from '@data/sanity/schema'
import { Keyed } from '@lib/helpers'
import { SanityBlogPostWithoutBody, SanityBlogPostOptions } from './blog'
import { SanityContentFragment } from './content'
import { SanityImageFragment } from './image'
import { SanityVideo } from './video'
import { HasFontCase, LinkType, SanityButtonStyle } from './link'

export enum SanityBlockType {
  FREEFORM = 'freeform',
  ACCORDIONS = 'accordions',
  VIDEO = 'video',
  NEWSLETTER = 'newsletter',
  BLOG_POST_CARD = 'blogPostCard',
  CONTENT_CAROUSEL = 'contentCarousel',
  CALENDLY_WIDGET = 'calendlyWidget',
  DEMO_FORM = 'demoForm',
  IFRAME = 'iframe',
  HTML = 'html',
  MEDIA_TEXT = 'mediaText',
}

export type SanityFreeformBlock = Keyed<
  Pick<Freeform, '_type' | 'textAlign' | 'maxWidth'> & {
    content: SanityContentFragment
  }
>

export type SanityFreeform = Omit<SanityFreeformBlock, '_type' | '_key'>

export type SanityAccordionsBlockItem = Pick<Accordion, 'title'> & {
  id: string
  content: SanityContentFragment
}

export type SanityAccordionsBlock = Keyed<
  Pick<Accordions, '_type'> & {
    items: Array<SanityAccordionsBlockItem>
  }
>

export type SanityAccordions = Omit<SanityAccordionsBlock, '_type' | '_key'>

export type SanityVideoBlock = Keyed<Pick<Video, '_type'> & SanityVideo>

export type SanityNewsletterBlock = Keyed<
  Pick<Newsletter, '_type' | 'klaviyoListID' | 'hubSpotFormId' | 'submit'> & {
    service: 'klaviyo' | 'hubSpot'
    variant?: 'basic' | 'extended'
    title?: string
    description?: string
    successMsg: SanityContentFragment
    errorMsg: SanityContentFragment
    terms?: SanityContentFragment
  }
>

export type SanityBlogPostCardBlock = Keyed<
  Pick<BlogPostCard, '_type'> & {
    post: SanityBlogPostWithoutBody
    options?: SanityBlogPostOptions
  }
>

export enum SanityContentCarouselItemType {
  FREEFORM = 'freeform',
  PHOTO = 'photo',
  BLOG_POST = 'blogPost',
  VIDEO = 'video',
  LINK_ITEM = 'linkItem',
}

interface SanityContentCarouselFreeformItem {
  _type: SanityContentCarouselItemType.FREEFORM
  freeform: SanityFreeformBlock
}

interface SanityContentCarouselPhotoItem {
  _type: SanityContentCarouselItemType.PHOTO
  photo: SanityImageFragment
}

interface SanityContentCarouselBlogPostItem {
  _type: SanityContentCarouselItemType.BLOG_POST
  blogPost: SanityBlogPostWithoutBody
}

type SanityContentCarouselVideoItem = {
  _type: SanityContentCarouselItemType.VIDEO
  video: SanityVideo
}

interface SanityContentCarouselLinkItemLinkPage {
  id: string
  type: LinkType
  slug?: string
}

export interface SanityContentCarouselLinkItemLink {
  title?: string
  page: SanityContentCarouselLinkItemLinkPage
  image: SanityImageFragment
}

interface SanityContentCarouselLinkItem {
  _type: SanityContentCarouselItemType.LINK_ITEM
  linkItem: SanityContentCarouselLinkItemLink
}

export type SanityContentCarouselItem = Keyed<
  | SanityContentCarouselFreeformItem
  | SanityContentCarouselPhotoItem
  | SanityContentCarouselBlogPostItem
  | SanityContentCarouselVideoItem
  | SanityContentCarouselLinkItem
>

export interface SanityContentCarouselBlock {
  _key: string
  _type: 'contentCarousel'
  items: Array<SanityContentCarouselItem>
  drag?: boolean
  loop?: boolean
  showArrows?: boolean
  autoSwitch?: boolean
  autoSwitchTimeout?: number
  previewNextSlide?: boolean
  showOverflow?: boolean
}

export type SanityCalendlyWidgetBlock = Keyed<
  Pick<
    CalendlyWidget,
    '_type' | 'url' | 'hideEventDetails' | 'hideCookieSettings'
  >
>

interface SanityDemoFormAnalytics {
  submitEventName?: string
}

export type SanityDemoFormBlock = Keyed<
  Pick<
    DemoForm,
    | '_type'
    | 'klaviyoListID'
    | 'klaviyoNewsletterListID'
    | 'hubSpotFormId'
    | 'hubSpotNewsletterFormId'
    | 'hidePhoneInput'
    | 'submit'
    | 'strings'
  > & {
    service: 'klaviyo' | 'hubSpot'
    successMsg: SanityContentFragment
    errorMsg: SanityContentFragment
    terms?: SanityContentFragment
    newsletterStatement?: SanityContentFragment
    analytics?: SanityDemoFormAnalytics
    buttonStyle?: SanityButtonStyle & HasFontCase
  }
>

export type SanityIframeBlock = Keyed<
  Pick<Iframe, '_type' | 'title' | 'url' | 'width' | 'height'>
>

export type SanityHtmlBlock = Keyed<Pick<Html, '_type' | 'code'>>

export interface SanityMediaTextBlock {
  _key: string
  _type: 'mediaText'
  mediaType: 'image' | 'video'
  image?: SanityImageFragment
  video?: SanityVideo
  content: SanityContentFragment
}

export type SanityBlocks =
  | SanityFreeformBlock
  | SanityAccordionsBlock
  | SanityVideoBlock
  | SanityNewsletterBlock
  | SanityBlogPostCardBlock
  | SanityContentCarouselBlock
  | SanityCalendlyWidgetBlock
  | SanityDemoFormBlock
  | SanityIframeBlock
  | SanityHtmlBlock
  | SanityMediaTextBlock
